import { useQuery } from 'react-query';
import TrackerAPI, { TasksResponse } from '@buildwise/libs/trackers/apis/TrackerAPI';
import useUserData from 'hooks/useUserData';
import { useMemo } from 'react';
import { AxiosResponse } from 'axios';
import { queryClient } from 'config/queryClient';
import {
  ArticleRelatedRecordType,
  AttachmentType,
  CustomFieldValueType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { LikedUserType } from '@buildwise/pages/article/hooks/useArticleDetailsQuery';
import { MentionType } from '@buildwise/pages/article/components/CommentsBox';
import { cloneDeep } from 'lodash';
import { trackerQueryKeys } from './query-keys';

export type TaskType = {
  id: string;
  companyId: string;
  createdAt: Date | string;
  createdBy: string;
  customFields?: Record<string, CustomFieldValueType | undefined>;
  description: string;
  dueDate: string;
  relatedRecords: ArticleRelatedRecordType[];
  status: string;
  title: string;
  trackerId: string;
  trackerType: string;
  type: string;
  updatedBy: string;
  updatedAt: string | Date;
  applicableTo: string;
  impact: string;
};

export type CommentReplyType = CommentType & { parentId: string };

export type CommentType = {
  attachments?: AttachmentType[];
  commentAuthor: string;
  commentAuthorDisplayName: string;
  commentAuthorDisplayTitle: string;
  commentText: string;
  commentedAt: Date | string;
  companyId: string;
  id: string;
  likedUsers: LikedUserType[];
  markedAnswer?: true;
  markedAnswerBy?: string;
  mentions: MentionType[];
  primaryCompanyName?: string;
  profileImageURL?: string;
  replies?: CommentReplyType[];
  taggedTo: string[];
  topicsAutoGenerated: any;
  updatedBy?: string;
  userPrimaryCompanyName?: string;
  objectId?: string;
  objectName?: string;
  objectTitle?: string;
  objectType?: string;
  objectSubType?: string;
  objectDescription?: string;
};

type CommentsObjectResponse = {
  commentsCount?: number;
  companyId: string;
  comments: CommentType[];
};

export const useTrackerTasksQuery = ({
  trackerId,
  page,
  size,
  shouldFetch = true,
}: {
  trackerId: string;
  page: number;
  size: number;
  shouldFetch?: boolean;
}) => {
  const { companyId } = useUserData();

  const { data, ...rest } = useQuery<AxiosResponse<TasksResponse>, Error>({
    queryKey: trackerQueryKeys.getAllTasksByTrackerId(companyId, trackerId, page, size),
    queryFn: () => TrackerAPI.getTasksPagination(companyId, trackerId, page, size),
    enabled: !!companyId && !!trackerId && shouldFetch,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  });

  const tasks = useMemo(() => data?.data?.tasks || [], [data]);

  // Helper to update the cached tasks list after mutation
  const updateTasksCache = (
    updatedTask: TaskType,
    action: 'update' | 'add' | 'delete' = 'update',
  ) => {
    const queryKey = trackerQueryKeys.getAllTasksByTrackerId(companyId, trackerId, page, size);
    const queryStatus = queryClient.getQueryState(queryKey);

    if (queryStatus?.status && queryStatus.status !== 'idle') {
      queryClient.setQueryData<AxiosResponse<TasksResponse> | undefined>(queryKey, (old) => {
        if (old) {
          let updatedTasks: TaskType[];
          if (action === 'update') {
            updatedTasks = old.data.tasks.map((task) =>
              task.id === updatedTask.id ? updatedTask : task,
            );
          } else if (action === 'add') {
            updatedTasks = [updatedTask, ...(old.data.tasks || [])];
          } else if (action === 'delete') {
            updatedTasks = old.data.tasks.filter((task) => task.id !== updatedTask.id);
          } else {
            updatedTasks = old.data.tasks;
          }
          return {
            ...old,
            data: {
              ...old.data,
              tasks: updatedTasks,
            },
          };
        }
        return old;
      });
    }
  };

  return {
    tasks,
    userMap: data?.data?.userMap || {},
    updateTasksCache,
    ...rest,
  };
};

export const useObjectCommentsQuery = ({
  objectId,
  objectName,
  objectType,
  shouldFetch = true,
}: {
  objectId: string;
  objectName: string;
  objectType: string;
  shouldFetch?: boolean;
}) => {
  const { companyId } = useUserData();
  const { data: commentsData, ...rest } = useQuery<AxiosResponse<CommentsObjectResponse>, Error>({
    queryKey: trackerQueryKeys.getCommentsByObjectId(companyId, objectId, objectName, objectType),
    queryFn: () => TrackerAPI.getCommentsByObjectId(companyId, objectId, objectName, objectType),
    enabled: !!companyId && shouldFetch,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  });

  const comments = useMemo(() => {
    return commentsData?.data?.comments || [];
  }, [commentsData]);

  // Helper to update the cached comments list after mutation
  const updateCommentsCache = (
    updatedComment: CommentType,
    action: 'add' | 'update' | 'like' | 'reply' | 'delete',
    commentPath: string = '',
  ) => {
    const queryKey = trackerQueryKeys.getCommentsByObjectId(
      companyId,
      objectId,
      objectName,
      objectType,
    );
    const queryStatus = queryClient.getQueryState(queryKey);

    if (queryStatus?.status && queryStatus.status !== 'idle') {
      queryClient.setQueryData<AxiosResponse<CommentsObjectResponse> | undefined>(
        queryKey,
        (oldData) => {
          if (oldData && updatedComment) {
            const oldComments = cloneDeep([...(oldData?.data?.comments || [])]);

            if (action === 'add') {
              oldComments.unshift(updatedComment);
            }

            if (commentPath) {
              let comments = oldComments;
              const commentIds = commentPath.split('/');

              for (let i = 0; i < commentIds.length; i++) {
                const commentIndex = comments.findIndex((comment) => comment.id === commentIds[i]);

                if (commentIndex === -1) {
                  break;
                }

                if (commentIndex > -1) {
                  if (i !== commentIds.length - 1) {
                    comments = comments[commentIndex]?.replies || [];
                  } else {
                    if (action === 'delete') {
                      comments.splice(commentIndex, 1);
                    }

                    if (action === 'update' || action === 'like') {
                      comments[commentIndex] = {
                        ...updatedComment,
                        replies: comments[commentIndex].replies,
                      };
                    }

                    if (action === 'reply') {
                      if (comments[commentIndex].replies) {
                        comments[commentIndex].replies?.unshift(updatedComment as CommentReplyType);
                      } else {
                        comments[commentIndex] = {
                          ...comments[commentIndex],
                          replies: [updatedComment as CommentReplyType],
                        };
                      }
                    }
                  }
                }
              }
            }
            return {
              ...oldData,
              data: {
                ...oldData.data,
                comments: [...oldComments],
                commentsCount: oldComments?.length || 0,
              },
            };
          }
          return oldData;
        },
      );
    }
  };

  return {
    comments,
    updateCommentsCache,
    ...rest,
  };
};
